"use client";

import { ChevronsUpDown, CircleSlash, Pencil, Plus, Trash2 } from "lucide-react";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import type { TreeRowNode } from "./category-tree";

interface CategoryRowMenuProps<T extends TreeRowNode<T>> {
  node: T;
  readOnly?: boolean;
  disabledByParent?: boolean;
  /** Trees whose nodes have no enable/disable control hide the item entirely. */
  showEnabled?: boolean;
  onAdd: (parent: T) => void;
  onRename: (node: T) => void;
  onExpandBranch: (node: T) => void;
  onToggleEnabled: (node: T) => void;
  onDelete: (node: T) => void;
}

/**
 * Row actions, so a category can be acted on without first selecting it and
 * waiting for its schema to load.
 */
export function CategoryRowMenu<T extends TreeRowNode<T>>({
  node,
  readOnly = false,
  disabledByParent = false,
  showEnabled = true,
  onAdd,
  onRename,
  onExpandBranch,
  onToggleEnabled,
  onDelete,
}: CategoryRowMenuProps<T>) {
  const actionLabel = node.isCanonical ? "Disable category" : "Enable category";
  return (
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuItem disabled={readOnly} onSelect={() => onAdd(node)}>
        <Plus className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
        Add subcategory
      </DropdownMenuItem>
      <DropdownMenuItem disabled={readOnly} onSelect={() => onRename(node)}>
        <Pencil className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
        Rename…
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={node.children.length === 0}
        onSelect={() => onExpandBranch(node)}
      >
        <ChevronsUpDown className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
        Expand all subcategories
      </DropdownMenuItem>
      {showEnabled && (
        <DropdownMenuItem disabled={readOnly || disabledByParent} onSelect={() => onToggleEnabled(node)}>
          <CircleSlash className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
          {disabledByParent ? "Disabled by parent" : actionLabel}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        disabled={readOnly}
        className="text-destructive focus:text-destructive"
        onSelect={() => onDelete(node)}
      >
        <Trash2 className="mr-2 h-3.5 w-3.5" />
        Delete…
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}
